// src/services/api/reflectionQuestions.ts
import { getFunctions, httpsCallable, HttpsCallableOptions } from "firebase/functions";
import { logger } from '../../utils/logger';

// 反思問題請求參數型別
export interface ReflectionQuestionsRequest {
  date: string;           // YYYY-MM-DD 格式
  language?: string;      // 可選，例如 'zh-TW', 'en-US'
  options?: {
    urgency?: 'low' | 'medium' | 'high';
  };
}

// 反思問題回應型別
export interface ReflectionQuestionsResponse {
  date: string;
  questions: string[];    // 3 個反思問題的陣列
  _meta?: {
    businessContext?: {
      taskType: string;
      complexity: string;
      urgency: string;
    };
    reflectionData?: {
      date: string;
      timeBoxesCount: number;
    };
  };
}

/**
 * 生成每日反思問題
 * 根據指定日期的時間箱數據，使用 AI 生成個人化的反思問題
 *
 * @param request - 請求參數
 * @param signal - 可選的 AbortSignal 用於取消請求
 * @returns 包含 3 個反思問題的回應
 */
export const generateReflectionQuestions = async (
  request: ReflectionQuestionsRequest,
  signal?: AbortSignal
): Promise<ReflectionQuestionsResponse> => {
  try {
    const functions = getFunctions();

    const options: HttpsCallableOptions = {};
    if (signal && signal.aborted) {
      throw new DOMException('Request aborted', 'AbortError');
    }

    const generateQuestionsFunc = httpsCallable(
      functions,
      'generateDailyReflectionQuestions',
      options
    );

    const params = {
      date: request.date,
      language: request.language,
      options: request.options || {}
    };

    const result = await generateQuestionsFunc(params);
    return result.data as ReflectionQuestionsResponse;

  } catch (error) {
    if (signal?.aborted) {
      throw new DOMException('Request aborted', 'AbortError');
    }
    logger.error('ReflectionQuestions', 'Failed to generate reflection questions', error);
    throw error;
  }
};

/**
 * 生成反思問題的簡化版本（不需要 signal）
 */
export const generateReflectionQuestionsSimple = async (
  date: string,
  language?: string
): Promise<ReflectionQuestionsResponse> => {
  return generateReflectionQuestions({ date, language });
};
